import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import useAuth from '../hooks/useAuth';
import permissionsAPI from '../api/permissions';

const RoleContext = createContext();

// ----------------------------------------------------------------
// Role display metadata (labels, colors, initials)
// ----------------------------------------------------------------
export const roleConfigs = {
  SuperAdmin:      { label: 'Super Admin',       initials: 'SA', color: '#d32f2f', defaultPath: '/dashboard' },
  Admin:           { label: 'Administrator',      initials: 'AD', color: '#1565c0', defaultPath: '/dashboard' },
  PurchaseManager: { label: 'Purchase Manager',   initials: 'PM', color: '#7b1fa2', defaultPath: '/purchase-orders' },
  Buyer:           { label: 'Buyer',              initials: 'BY', color: '#ed6c02', defaultPath: '/purchase-orders' },
  InventoryManager:{ label: 'Inventory Manager',  initials: 'IM', color: '#00838f', defaultPath: '/inventory' },
  StoreKeeper:     { label: 'Store Keeper',       initials: 'SK', color: '#2e7d32', defaultPath: '/goods-receipts' },
  Sales:           { label: 'Sales',              initials: 'SL', color: '#c62828', defaultPath: '/sales-orders' },
  Employee:        { label: 'Employee',           initials: 'EM', color: '#5f6b7a', defaultPath: '/dashboard' },
  Supplier:        { label: 'Supplier',           initials: 'SP', color: '#ff6b35', defaultPath: '/supplier-portal' },
  Customer:        { label: 'Customer',           initials: 'CU', color: '#4caf50', defaultPath: '/dashboard' },
  User:            { label: 'User',               initials: 'US', color: '#9e9e9e', defaultPath: '/dashboard' },
};

// ----------------------------------------------------------------
// Path → required permission mapping for sidebar visibility
// ----------------------------------------------------------------
const PATH_PERMISSION_MAP = {
  '/dashboard':            'dashboard.view',
  '/products':             'products.view',
  '/suppliers':            'suppliers.view',
  '/customers':            'customers.view',
  '/plants':               'plants.view',
  '/rfqs':                 'rfqs.view',
  '/contracts':            'contracts.view',
  '/purchase-orders':      'purchaseOrders.view',
  '/supplier-portal-manage': 'supplierPortal.view',
  '/quotations':           'quotations.view',
  '/sales-orders':         'salesOrders.view',
  '/credit-notes':         'creditNotes.view',
  '/debit-notes':          'debitNotes.view',
  '/inventory':            'inventory.view',
  '/goods-receipts':       'goodsReceipts.view',
  '/reconciliation':       'reconciliation.view',
  '/returns':              'returns.view',
  '/document-templates':   'templates.view',
  '/goods-issues':         'goodsIssues.view',
  '/transfer-postings':    'transferPostings.view',
  '/physical-inventory':   'physicalInventory.view',
  '/reports/inventory':    'reports.inventory',
  '/reports/sales':        'reports.sales',
  '/reports/procurement':  'reports.procurement',
  '/reports/category-analysis': 'reports.inventory',
  '/approvals/pending':    'approvals.view',
  '/admin':                'admin.users',
  '/profile':              null, // always accessible
};

// ----------------------------------------------------------------
// Provider
// ----------------------------------------------------------------
export const RoleProvider = ({ children }) => {
  const { user, isAuthenticated, isSuperAdmin } = useAuth();
  const [permissions, setPermissions] = useState(() => {
    // Restore from localStorage for instant rendering on page refresh
    try {
      const cached = localStorage.getItem('userPermissions');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const loadPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions([]);
      setPermissionsLoaded(true);
      return;
    }
    try {
      const res = await permissionsAPI.getMyPermissions();
      const perms = res.data?.permissions || [];
      setPermissions(perms);
      localStorage.setItem('userPermissions', JSON.stringify(perms));
    } catch {
      // Fall back to cached if API fails
    } finally {
      setPermissionsLoaded(true);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Clear on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setPermissions([]);
      localStorage.removeItem('userPermissions');
    }
  }, [isAuthenticated]);

  const role = user?.role || 'User';
  const config = roleConfigs[role] || roleConfigs.User;

  // SuperAdmin and Admin always have access to everything
  const isFullAccess = isSuperAdmin || role === 'Admin';

  // Check a single permission string e.g. can('products.create')
  const can = (permission) => {
    if (!user) return false;
    if (isFullAccess) return true;
    return permissions.includes(permission);
  };

  // Check sidebar path visibility
  const hasAccess = (path) => {
    if (!user) return false;
    if (isFullAccess) return true;
    // /profile and unknown paths are always accessible
    const requiredPerm = PATH_PERMISSION_MAP[path];
    if (requiredPerm === undefined) return true; // unmapped path
    if (requiredPerm === null) return true;        // explicitly open
    return permissions.includes(requiredPerm);
  };

  // Backward-compatible canPerformAction(action, module)
  // action: 'create'|'edit'|'delete'|'approve'|'view'
  // module: 'products'|'rfqs'|etc.
  const canPerformAction = (action, module) => {
    if (!user) return false;
    if (isFullAccess) return true;
    const perm = `${module}.${action}`;
    return permissions.includes(perm);
  };

  const value = {
    role,
    config,
    roleConfigs,
    permissions,
    permissionsLoaded,
    can,
    hasAccess,
    canPerformAction,
    refreshPermissions: loadPermissions,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) throw new Error('useRole must be used within a RoleProvider');
  return context;
};

export default RoleContext;
